import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  AlertCircle,
  Check,
  Copy,
  ChevronDown,
  ChevronUp,
  Layers,
  Wand2,
  Eye,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Regex,
  Play,
} from 'lucide-react';
import {
  workflows,
  AdminWorkflow,
  UpdateWorkflowRequest,
  imageBackends,
  ImageBackend,
  PromptReplacement,
  TestReplacementsResponse,
} from '../../api/client';
import { cn } from '../../lib/utils';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';

interface PlaceholderInfo {
  placeholder: string;
  description: string;
}

const PLACEHOLDERS: PlaceholderInfo[] = [
  { placeholder: '%prompt%', description: 'Combined prompt from iOS (required)' },
  { placeholder: '%negative_prompt%', description: 'Negative prompt (default: empty)' },
  { placeholder: '%seed%', description: 'Random seed (auto-generated)' },
  { placeholder: '%width%', description: 'Image width from iOS (required)' },
  { placeholder: '%height%', description: 'Image height from iOS (required)' },
  { placeholder: '%steps%', description: 'Sampling steps' },
  { placeholder: '%scale%', description: 'CFG scale' },
  { placeholder: '%model%', description: 'Model checkpoint name' },
  { placeholder: '%sampler%', description: 'Sampler name' },
  { placeholder: '%scheduler%', description: 'Scheduler name' },
];

export default function WorkflowEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Workflow state
  const [workflow, setWorkflow] = useState<AdminWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Metadata form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [includeContext, setIncludeContext] = useState(false);
  const [backendId, setBackendId] = useState('');

  // Prompt transformer state
  const [promptTransformEnabled, setPromptTransformEnabled] = useState(false);
  const [promptTransformModel, setPromptTransformModel] = useState('');
  const [promptTransformSystemPrompt, setPromptTransformSystemPrompt] = useState('');
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [previewDuration, setPreviewDuration] = useState('10');
  const [previewAudio, setPreviewAudio] = useState(true);
  const [previewMultishot, setPreviewMultishot] = useState(false);

  // ─── Prompt Replacements state ───
  // Silent background regex find/replace rules applied to the positive prompt.
  // Fail-open: rule errors never block generation. See worker/src/image/prompt-replacements.ts
  const [rules, setRules] = useState<PromptReplacement[]>([]);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesSuccess, setRulesSuccess] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [testInput, setTestInput] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<TestReplacementsResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // Backends state
  const [backends, setBackends] = useState<ImageBackend[]>([]);
  const [backendsLoading, setBackendsLoading] = useState(true);

  // Save state for metadata
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaSuccess, setMetaSuccess] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);

  // Save state for JSON / provider params
  const [jsonSaving, setJsonSaving] = useState(false);
  const [jsonSuccess, setJsonSuccess] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonValidationError, setJsonValidationError] = useState<string | null>(null);

  // Placeholder reference panel
  const [placeholdersExpanded, setPlaceholdersExpanded] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Provider params state (Atlas/Groq structured form)
  const [ppModel, setPpModel] = useState('');
  const [ppNegativePrompt, setPpNegativePrompt] = useState('');
  const [ppPromptExpansion, setPpPromptExpansion] = useState(false);

  // CodeMirror refs
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const initialJsonRef = useRef<string>('');
  const jsonValidationErrorRef = useRef<string | null>(null);
  jsonValidationErrorRef.current = jsonValidationError;

  // Fetch workflow data
  const fetchWorkflow = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setNotFound(false);
      setWorkflow(null);
      setMetaError(null);
      setJsonError(null);
      const res = await workflows.get(parseInt(id));
      const wf = res.workflow;
      setWorkflow(wf);

      // Populate form fields
      setName(wf.name);
      setSlug(wf.slug);
      setDescription(wf.description || '');
      setIcon(wf.icon || '');
      setIsEnabled(wf.isEnabled);
      setIncludeContext(wf.includeContext);
      setBackendId(wf.backendId || '');
      setPromptTransformEnabled(wf.promptTransformEnabled);
      setPromptTransformModel(wf.promptTransformModel || '');
      setPromptTransformSystemPrompt(wf.promptTransformSystemPrompt || '');

      // Store initial JSON for editor initialization
      let formattedJson = wf.workflowJson || '{}';
      try {
        formattedJson = JSON.stringify(JSON.parse(formattedJson), null, 2);
      } catch {
        // If it's not valid JSON, use as-is
      }
      initialJsonRef.current = formattedJson;

      // Populate provider params if available
      if (wf.providerParams) {
        try {
          const pp = JSON.parse(wf.providerParams);
          setPpModel(pp.model || '');
          setPpNegativePrompt(pp.negative_prompt || '');
          setPpPromptExpansion(pp.prompt_extend ?? pp.enable_prompt_expansion ?? false);
        } catch {
          setJsonError('Stored provider_params is invalid JSON. Saving Provider Settings will replace it with the values shown below.');
        }
      }

      // Populate prompt replacement rules if available
      if (wf.promptReplacements && wf.promptReplacements.trim() !== '') {
        try {
          const parsed = JSON.parse(wf.promptReplacements);
          if (Array.isArray(parsed)) {
            setRules(parsed as PromptReplacement[]);
          } else {
            setRules([]);
          }
        } catch {
          setRules([]);
        }
      } else {
        setRules([]);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch workflow';
      if (message.includes('not found') || message.includes('404')) {
        setNotFound(true);
      } else {
        setMetaError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch backends
  const fetchBackends = useCallback(async () => {
    try {
      setBackendsLoading(true);
      const res = await imageBackends.list();
      setBackends(res.backends);
    } catch (error) {
      console.error('Failed to fetch backends:', error);
      setBackends([]);
    } finally {
      setBackendsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflow();
    fetchBackends();
  }, [fetchWorkflow, fetchBackends]);

  // Initialize CodeMirror editor once workflow data is loaded
  useEffect(() => {
    if (!editorRef.current || loading || notFound || !workflow) return;

    // Destroy existing editor if any
    if (viewRef.current) {
      viewRef.current.destroy();
      viewRef.current = null;
    }

    const state = EditorState.create({
      doc: initialJsonRef.current,
      extensions: [
        basicSetup,
        json(),
        oneDark,
        EditorView.theme({
          '&': { height: '100%' },
          '.cm-scroller': { overflow: 'auto' },
          '.cm-gutters': {
            backgroundColor: 'hsl(222 47% 8%)',
            borderRight: '1px solid hsl(222 30% 16%)',
          },
        }),
        EditorView.updateListener.of(() => {
          // Clear validation error when user edits
          if (jsonValidationErrorRef.current) {
            setJsonValidationError(null);
          }
        }),
      ],
    });

    viewRef.current = new EditorView({
      state,
      parent: editorRef.current,
    });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // Only re-init editor when loading completes — NOT when workflow object reference changes
    // (post-save refreshes change the reference but should not reset the editor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, notFound]);

  // Get current JSON from editor
  const getCurrentJson = useCallback(() => {
    return viewRef.current?.state.doc.toString() || '';
  }, []);

  // Save metadata
  const handleSaveMetadata = async () => {
    if (!workflow) return;

    // Validate backend is selected
    if (!backendId) {
      setMetaError('Backend is required');
      return;
    }

    setMetaSaving(true);
    setMetaError(null);
    setMetaSuccess(false);

    try {
      const data: UpdateWorkflowRequest = {
        name,
        description,
        icon,
        is_enabled: isEnabled,
        include_context: includeContext,
        backend_id: backendId,
        prompt_transform_enabled: promptTransformEnabled,
        prompt_transform_model: promptTransformModel,
        prompt_transform_system_prompt: promptTransformSystemPrompt,
      };

      // Only include slug if not permanent
      if (!workflow.isPermanent) {
        data.slug = slug;
      }

      await workflows.update(workflow.id, data);
      setMetaSuccess(true);
      setTimeout(() => setMetaSuccess(false), 3000);

      // Refresh workflow data
      const res = await workflows.get(workflow.id);
      setWorkflow(res.workflow);
      setBackendId(res.workflow.backendId || '');

      // Keep initialJsonRef in sync with server state
      if (res.workflow.workflowJson) {
        try {
          initialJsonRef.current = JSON.stringify(JSON.parse(res.workflow.workflowJson), null, 2);
        } catch {
          initialJsonRef.current = res.workflow.workflowJson;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save metadata';
      setMetaError(message);
    } finally {
      setMetaSaving(false);
    }
  };

  // Save JSON
  const handleSaveJson = async () => {
    if (!workflow) return;

    const currentJson = getCurrentJson();

    // Validate JSON
    try {
      JSON.parse(currentJson);
    } catch (e) {
      const parseError = e instanceof SyntaxError ? e.message : 'Invalid JSON';
      setJsonValidationError(parseError);
      return;
    }

    setJsonSaving(true);
    setJsonError(null);
    setJsonSuccess(false);
    setJsonValidationError(null);

    try {
      await workflows.update(workflow.id, { workflow_json: currentJson });
      setJsonSuccess(true);
      setTimeout(() => setJsonSuccess(false), 3000);

      // Keep initialJsonRef in sync so any future editor re-init uses saved content
      initialJsonRef.current = currentJson;

      // Refresh workflow data
      const res = await workflows.get(workflow.id);
      setWorkflow(res.workflow);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save workflow JSON';
      setJsonError(message);
    } finally {
      setJsonSaving(false);
    }
  };

  // Copy placeholder to clipboard
  const handleCopyPlaceholder = async (placeholder: string, index: number) => {
    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = placeholder;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  const selectableBackends = useMemo(() => {
    const activeBackends = backends.filter(b => b.isActive);
    const selectedBackend = backends.find(b => b.id === backendId);

    if (selectedBackend && !activeBackends.some(b => b.id === selectedBackend.id)) {
      return [...activeBackends, selectedBackend];
    }

    return activeBackends;
  }, [backends, backendId]);

  // Get the selected backend's type
  const selectedBackendType = backends.find(b => b.id === backendId)?.type;
  // Structured backends use provider_params JSON instead of ComfyUI workflow_json
  const isStructuredBackend = selectedBackendType === 'atlas' || selectedBackendType === 'groq' || selectedBackendType === 'nanogpt';
  const isAtlas = selectedBackendType === 'atlas';
  const isWanVideoWorkflow = isAtlas && workflow?.slug === 'wan-video';
  const isAtlasImageWorkflow = isAtlas && !isWanVideoWorkflow;

  // Build provider_params JSON from structured form state
  const buildProviderParams = useCallback(() => {
    const params: Record<string, unknown> = {};
    // Wan video model selection is operational state owned by the dedicated
    // admin control. Never copy it into provider_params.
    if (ppModel && !isWanVideoWorkflow) params.model = ppModel;
    if (ppNegativePrompt) params.negative_prompt = ppNegativePrompt;
    if (isAtlas) {
      if (isAtlasImageWorkflow) {
        params.prompt_extend = ppPromptExpansion;
      }
    }
    return JSON.stringify(params);
  }, [ppModel, ppNegativePrompt, ppPromptExpansion, isAtlas, isAtlasImageWorkflow, isWanVideoWorkflow]);

  // ─── Prompt Replacements: rule CRUD + save + test ───

  const makeNewRule = (): PromptReplacement => ({
    id: (crypto.randomUUID ? crypto.randomUUID() : `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    name: '',
    pattern: '',
    replacement: '',
    isRegex: false,
    caseInsensitive: false,
    multiline: false,
    dotAll: false,
    unicode: false,
    enabled: true,
    stage: 'pre_transform',
  });

  const handleAddRule = () => {
    setRules(prev => [...prev, makeNewRule()]);
  };

  const handleUpdateRule = (id: string, patch: Partial<PromptReplacement>) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  const handleDeleteRule = (id: string) => {
    setRules(prev => prev.filter(r => r.id !== id));
  };

  const handleMoveRule = (id: string, direction: -1 | 1) => {
    setRules(prev => {
      const idx = prev.findIndex(r => r.id === id);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      next.splice(newIdx, 0, moved);
      return next;
    });
  };

  const handleSaveRules = async () => {
    if (!workflow) return;

    setRulesSaving(true);
    setRulesError(null);
    setRulesSuccess(false);

    try {
      // Empty array → null (clears the column to avoid storing "[]" indefinitely).
      const payload: string | null = rules.length === 0 ? null : JSON.stringify(rules);
      await workflows.update(workflow.id, { prompt_replacements: payload });
      setRulesSuccess(true);
      setTimeout(() => setRulesSuccess(false), 3000);

      // Re-fetch to sync canonical state
      const res = await workflows.get(workflow.id);
      setWorkflow(res.workflow);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save prompt replacements';
      setRulesError(message);
    } finally {
      setRulesSaving(false);
    }
  };

  const handleRunTest = async () => {
    setTestRunning(true);
    setTestError(null);
    setTestResult(null);
    try {
      // Always use the preview endpoint — runs the CURRENT (possibly unsaved)
      // rules via the authoritative server-side code path.
      const res = await workflows.testReplacementsPreview({
        rules,
        input: testInput,
        stage: 'both',
      });
      setTestResult(res);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Test fire failed';
      setTestError(message);
    } finally {
      setTestRunning(false);
    }
  };

  // Per-rule regex validity for inline feedback. Compiled client-side using the
  // same engine family (V8) as the Worker, so behavior matches in practice.
  const ruleRegexErrors: Record<string, string | null> = useMemo(() => {
    const result: Record<string, string | null> = {};
    for (const rule of rules) {
      if (!rule.pattern) {
        result[rule.id] = null;
        continue;
      }
      try {
        const source = rule.isRegex ? rule.pattern : rule.pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        let flags = 'g';
        if (rule.caseInsensitive) flags += 'i';
        if (rule.multiline) flags += 'm';
        if (rule.dotAll) flags += 's';
        if (rule.unicode) flags += 'u';
        // eslint-disable-next-line no-new
        new RegExp(source, flags);
        result[rule.id] = null;
      } catch (e) {
        result[rule.id] = e instanceof Error ? e.message : 'Invalid regex';
      }
    }
    return result;
  }, [rules]);

  // Save provider params (structured form)
  const handleSaveProviderParams = async () => {
    if (!workflow) return;

    setJsonSaving(true);
    setJsonError(null);
    setJsonSuccess(false);

    try {
      await workflows.update(workflow.id, { provider_params: buildProviderParams() });
      setJsonSuccess(true);
      setTimeout(() => setJsonSuccess(false), 3000);

      const res = await workflows.get(workflow.id);
      setWorkflow(res.workflow);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save provider params';
      setJsonError(message);
    } finally {
      setJsonSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading workflow...</span>
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-6">
        <div className="card-glow rounded-2xl p-12 text-center animate-scale-in">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Workflow Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The workflow you are looking for does not exist or has been deleted.
          </p>
          <button
            onClick={() => navigate('/image/workflows')}
            className="btn-secondary px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-6">
        <div className="card-glow rounded-2xl p-12 text-center animate-scale-in max-w-lg">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load Workflow</h2>
          <p className="text-muted-foreground mb-6">
            {metaError || 'The workflow could not be loaded. Please try again.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={fetchWorkflow}
              className="btn-glow-violet px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={() => navigate('/image/workflows')}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Workflows
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/image/workflows')}
          className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Back to Workflows"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center text-xl">
            {workflow.icon || '\u26A1'}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gradient-violet">{workflow.name}</h1>
            <code className="text-xs font-mono text-muted-foreground">{workflow.slug}</code>
          </div>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" style={{ minHeight: 'calc(100vh - 200px)' }}>
        {/* Left Panel — Metadata */}
        <div className="xl:col-span-1 space-y-6">
          <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <Layers className="w-4.5 h-4.5 text-violet-400" />
              </div>
              <h2 className="text-lg font-semibold">Metadata</h2>
            </div>

            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                  placeholder="Workflow name"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={workflow.isPermanent}
                  className={cn(
                    'input-neural w-full px-4 py-3 rounded-xl text-sm font-mono',
                    workflow.isPermanent && 'opacity-60 cursor-not-allowed'
                  )}
                  placeholder="workflow-slug"
                />
                {workflow.isPermanent && (
                  <p className="text-xs text-amber-400/80 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Slug cannot be changed for permanent workflows
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm resize-none"
                  rows={3}
                  placeholder="Describe what this workflow does..."
                />
              </div>

              {/* Icon */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                  placeholder="\u26A1"
                />
              </div>

              {/* Backend Selector */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Backend <span className="text-rose-400">*</span>
                </label>
                {backendsLoading ? (
                  <div className="input-neural w-full px-4 py-3 rounded-xl text-sm flex items-center gap-2 text-muted-foreground">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Loading backends...</span>
                  </div>
                ) : selectableBackends.length === 0 ? (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-300">No active backends available</p>
                        <p className="text-xs text-amber-300/70 mt-1">
                          Create and activate a backend in the Backends page first
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <select
                      value={backendId}
                      onChange={(e) => setBackendId(e.target.value)}
                      className={cn(
                        'input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer',
                        !backendId && 'text-muted-foreground'
                      )}
                    >
                      <option value="">Select a backend...</option>
                      {selectableBackends.map((backend) => (
                        <option key={backend.id} value={backend.id}>
                          {backend.name} ({backend.type}){!backend.isActive ? ' (inactive)' : ''}
                        </option>
                      ))}
                    </select>
                    {!backendId && (
                      <p className="text-xs text-rose-400/80 mt-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Backend is required before saving or enabling workflow
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Enabled toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Enabled</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Allow this workflow to be used for generation
                  </p>
                </div>
                <button
                  onClick={() => setIsEnabled(!isEnabled)}
                  className={cn(
                    'relative w-11 h-6 rounded-full transition-colors duration-200',
                    isEnabled
                      ? 'bg-emerald-500'
                      : 'bg-secondary border border-border'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 shadow-sm',
                      isEnabled && 'translate-x-5'
                    )}
                  />
                </button>
              </div>

              {/* Include Chat Context toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Include Chat Context</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Prepend user + assistant messages to the image prompt
                  </p>
                </div>
                <button
                  onClick={() => setIncludeContext(!includeContext)}
                  className={cn(
                    'relative w-11 h-6 rounded-full transition-colors duration-200',
                    includeContext
                      ? 'bg-violet-500'
                      : 'bg-secondary border border-border'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 shadow-sm',
                      includeContext && 'translate-x-5'
                    )}
                  />
                </button>
              </div>

              {/* ─── Prompt Transformer Section ─── */}
              <div className="border-t border-border/30 pt-5 mt-5">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-amber-400" />
                    <div>
                      <p className="text-sm font-medium">Prompt Transformer</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Rewrite prompts with an LLM before generation
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setPromptTransformEnabled(!promptTransformEnabled)}
                    className={cn(
                      'relative w-11 h-6 rounded-full transition-colors duration-200',
                      promptTransformEnabled
                        ? 'bg-amber-500'
                        : 'bg-secondary border border-border'
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 shadow-sm',
                        promptTransformEnabled && 'translate-x-5'
                      )}
                    />
                  </button>
                </div>

                {promptTransformEnabled && (
                  <div className="space-y-4 mt-4 animate-fade-in-up">
                    {/* Model */}
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-2">
                        Model <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={promptTransformModel}
                        onChange={(e) => setPromptTransformModel(e.target.value)}
                        className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
                        placeholder="google/gemini-2.0-flash-001"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        OpenRouter model ID for prompt rewriting
                      </p>
                    </div>

                    {/* System Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-2">
                        System Prompt <span className="text-rose-400">*</span>
                      </label>
                      <textarea
                        value={promptTransformSystemPrompt}
                        onChange={(e) => setPromptTransformSystemPrompt(e.target.value)}
                        className="input-neural w-full px-4 py-3 rounded-xl text-sm resize-y"
                        rows={6}
                        placeholder="You are an expert at writing image generation prompts. Rewrite the user's prompt to be more detailed and descriptive for image generation. Output ONLY the rewritten prompt, nothing else."
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The image prompt will be sent as the user message
                      </p>
                    </div>

                    {/* Video Placeholder Reference */}
                    <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4">
                      <h4 className="text-sm font-medium text-violet-300 mb-2">Video Placeholders</h4>
                      <div className="space-y-1.5 text-xs text-muted-foreground">
                        <div className="flex gap-2">
                          <code className="text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded font-mono">{'{{duration}}'}</code>
                          <span>→ 5, 10, or 15 (seconds)</span>
                        </div>
                        <div className="flex gap-2">
                          <code className="text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded font-mono">{'{{audio}}'}</code>
                          <span>→ enabled or disabled</span>
                        </div>
                        <div className="flex gap-2">
                          <code className="text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded font-mono">{'{{multishot}}'}</code>
                          <span>→ enabled or disabled</span>
                        </div>
                      </div>

                      {/* Preview Toggle */}
                      <div className="mt-3 pt-3 border-t border-violet-500/20">
                        <button
                          onClick={() => setShowPromptPreview(!showPromptPreview)}
                          className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1.5 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {showPromptPreview ? 'Hide Preview' : 'Show Preview'}
                        </button>

                        {showPromptPreview && (
                          <div className="mt-3 space-y-3">
                            {/* Preview controls */}
                            <div className="flex flex-wrap gap-3">
                              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                Duration:
                                <select
                                  value={previewDuration}
                                  onChange={(e) => setPreviewDuration(e.target.value)}
                                  className="bg-background/50 border border-border/50 rounded px-1.5 py-0.5 text-xs text-foreground"
                                >
                                  <option value="5">5s</option>
                                  <option value="10">10s</option>
                                  <option value="15">15s</option>
                                </select>
                              </label>
                              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <input
                                  type="checkbox"
                                  checked={previewAudio}
                                  onChange={(e) => setPreviewAudio(e.target.checked)}
                                  className="rounded"
                                />
                                Audio
                              </label>
                              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <input
                                  type="checkbox"
                                  checked={previewMultishot}
                                  onChange={(e) => setPreviewMultishot(e.target.checked)}
                                  className="rounded"
                                />
                                Multishot
                              </label>
                            </div>

                            {/* Preview output */}
                            <div className="bg-background/50 border border-border/30 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1.5 font-medium">Resolved system prompt:</p>
                              <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed">
                                {promptTransformSystemPrompt
                                  .replace(/\{\{duration\}\}/g, previewDuration)
                                  .replace(/\{\{audio\}\}/g, previewAudio ? 'enabled' : 'disabled')
                                  .replace(/\{\{multishot\}\}/g, previewMultishot ? 'enabled' : 'disabled')
                                  .trim() || '(empty system prompt)'}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Meta error */}
              {metaError && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 animate-scale-in">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-rose-300">{metaError}</p>
                  </div>
                </div>
              )}

              {/* Meta success */}
              {metaSuccess && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 animate-scale-in">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <p className="text-sm text-emerald-300">Metadata saved successfully</p>
                  </div>
                </div>
              )}

              {/* Save Metadata Button */}
              <button
                onClick={handleSaveMetadata}
                disabled={metaSaving || !name.trim() || !backendId}
                className={cn(
                  'btn-glow-violet w-full px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2',
                  (metaSaving || !name.trim() || !backendId) && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="flex items-center gap-2">
                  {metaSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {metaSaving ? 'Saving...' : 'Save Metadata'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel — JSON Editor (RunPod/Local) or Structured Form (Atlas/Groq) */}
        <div className="xl:col-span-2 flex flex-col gap-4">
          {isStructuredBackend ? (
            /* ─── Structured Provider Params Form (Atlas/Groq) ─── */
            <div
              className="card-glow rounded-2xl flex flex-col overflow-hidden animate-fade-in-up"
              style={{ animationDelay: '200ms' }}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">Provider Settings</h2>
                  <span className="px-2 py-0.5 text-xs font-mono rounded-md bg-secondary text-muted-foreground">
                    {isAtlas ? 'Atlas Cloud' : 'Groq Imagine'}
                  </span>
                </div>
                <button
                  onClick={handleSaveProviderParams}
                  disabled={jsonSaving}
                  className={cn(
                    'btn-glow-violet px-4 py-2 rounded-xl text-sm flex items-center gap-2',
                    jsonSaving && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <span className="flex items-center gap-2">
                    {jsonSaving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {jsonSaving ? 'Saving...' : 'Save Settings'}
                  </span>
                </button>
              </div>

              {/* Success/Error Messages */}
              {jsonSuccess && (
                <div className="mx-6 mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 animate-scale-in">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <p className="text-sm text-emerald-300">Settings saved successfully</p>
                  </div>
                </div>
              )}
              {jsonError && (
                <div className="mx-6 mt-3 bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 animate-scale-in">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-rose-300">{jsonError}</p>
                  </div>
                </div>
              )}

              <div className="p-6 space-y-5">
                {/* Model */}
                {isWanVideoWorkflow ? (
                  <div className="rounded-xl bg-violet-500/10 border border-violet-500/30 p-4">
                    <p className="text-sm font-medium text-violet-200">Model is controlled from the Image Dashboard</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      The live selector is stored separately so saving this workflow cannot overwrite the active model.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/image')}
                      className="btn-secondary px-3 py-2 rounded-lg text-xs mt-3"
                    >
                      Open live model control
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      Model
                    </label>
                    {isAtlas ? (
                      <select
                        value={ppModel}
                        onChange={(e) => setPpModel(e.target.value)}
                        className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
                      >
                        <option value="">Select a model...</option>
                        <option value="z-image/turbo">Z-Image Turbo</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={ppModel}
                        onChange={(e) => setPpModel(e.target.value)}
                        className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
                        placeholder="grok-2-image"
                      />
                    )}
                  </div>
                )}

                {/* Default Negative Prompt */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Default Negative Prompt
                  </label>
                  <textarea
                    value={ppNegativePrompt}
                    onChange={(e) => setPpNegativePrompt(e.target.value)}
                    className="input-neural w-full px-4 py-3 rounded-xl text-sm resize-none"
                    rows={2}
                    placeholder="text, watermark, worst quality, low quality"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    iOS can override this per-generation
                  </p>
                </div>

                {/* Atlas-specific settings */}
                {isAtlas && (
                  <>
                    {isAtlasImageWorkflow && (
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium">Prompt Expansion</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Let Atlas expand the prompt for better results
                          </p>
                        </div>
                        <button
                          onClick={() => setPpPromptExpansion(!ppPromptExpansion)}
                          className={cn(
                            'relative w-11 h-6 rounded-full transition-colors duration-200',
                            ppPromptExpansion
                              ? 'bg-emerald-500'
                              : 'bg-secondary border border-border'
                          )}
                        >
                          <span className={cn(
                            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 shadow-sm',
                            ppPromptExpansion && 'translate-x-5'
                          )} />
                        </button>
                      </div>
                    )}

                    {isWanVideoWorkflow && (
                      <div className="rounded-xl bg-secondary/50 border border-border/50 p-4">
                        <p className="text-sm font-medium">Video options come from the app</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Duration, resolution, prompt expansion, shot type, and audio are sent on every iOS video request, so workflow defaults cannot override them. The default negative prompt above remains active when iOS omits it.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Preview of generated JSON */}
                <div className="mt-4 p-4 rounded-xl bg-secondary/50 border border-border/50">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Generated provider_params:</p>
                  <pre className="text-xs font-mono text-violet-400 whitespace-pre-wrap break-all">
                    {JSON.stringify(JSON.parse(buildProviderParams()), null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            /* ─── JSON Editor (RunPod/Local) ─── */
            <>
              {/* Placeholder Reference Panel (Collapsible) */}
              <div
                className="card-glow rounded-2xl overflow-hidden animate-fade-in-up"
                style={{ animationDelay: '200ms' }}
              >
                <button
                  onClick={() => setPlaceholdersExpanded(!placeholdersExpanded)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Copy className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">Placeholder Reference</p>
                      <p className="text-xs text-muted-foreground">
                        {PLACEHOLDERS.length} available placeholders for workflow JSON
                      </p>
                    </div>
                  </div>
                  {placeholdersExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>

                {placeholdersExpanded && (
                  <div className="px-6 pb-5 border-t border-border/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                      {PLACEHOLDERS.map((item, index) => (
                        <div
                          key={item.placeholder}
                          className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-secondary/50 group"
                        >
                          <div className="flex-1 min-w-0">
                            <code className="text-xs font-mono text-violet-400">
                              {item.placeholder}
                            </code>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {item.description}
                            </p>
                          </div>
                          <button
                            onClick={() => handleCopyPlaceholder(item.placeholder, index)}
                            className={cn(
                              'p-1.5 rounded-md transition-all flex-shrink-0',
                              copiedIndex === index
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80'
                            )}
                            title={copiedIndex === index ? 'Copied!' : 'Copy to clipboard'}
                          >
                            {copiedIndex === index ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Editor Card */}
              <div
                className="card-glow rounded-2xl flex flex-col flex-1 overflow-hidden animate-fade-in-up"
                style={{ animationDelay: '300ms' }}
              >
                {/* Editor Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">Workflow JSON</h2>
                    <span className="px-2 py-0.5 text-xs font-mono rounded-md bg-secondary text-muted-foreground">
                      ComfyUI API Format
                    </span>
                  </div>
                  <button
                    onClick={handleSaveJson}
                    disabled={jsonSaving}
                    className={cn(
                      'btn-glow-violet px-4 py-2 rounded-xl text-sm flex items-center gap-2',
                      jsonSaving && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {jsonSaving ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {jsonSaving ? 'Saving...' : 'Save JSON'}
                    </span>
                  </button>
                </div>

                {/* JSON Success Message */}
                {jsonSuccess && (
                  <div className="mx-6 mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 animate-scale-in">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <p className="text-sm text-emerald-300">Workflow JSON saved successfully</p>
                    </div>
                  </div>
                )}

                {/* JSON Error Message */}
                {jsonError && (
                  <div className="mx-6 mt-3 bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 animate-scale-in">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-rose-300">{jsonError}</p>
                    </div>
                  </div>
                )}

                {/* JSON Validation Error */}
                {jsonValidationError && (
                  <div className="mx-6 mt-3 bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 animate-scale-in">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-rose-400">Invalid JSON</p>
                        <p className="text-xs text-rose-300/80 mt-1 font-mono">{jsonValidationError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* CodeMirror Editor */}
                <div
                  ref={editorRef}
                  className="flex-1 overflow-hidden"
                  style={{ minHeight: '500px' }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ─── Prompt Replacements (full-width card) ─── */}
      <div
        className="card-glow rounded-2xl p-6 animate-fade-in-up"
        style={{ animationDelay: '400ms' }}
      >
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-rose-500/10 flex items-center justify-center">
              <Regex className="w-4.5 h-4.5 text-rose-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Prompt Replacements</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Silent regex find/replace applied to the positive prompt. Fail-open &mdash; rule errors never block generation. Applies to this workflow only.
              </p>
            </div>
          </div>
          <button
            onClick={handleSaveRules}
            disabled={rulesSaving}
            className={cn(
              'btn-glow-violet px-4 py-2 rounded-xl text-sm flex items-center gap-2',
              rulesSaving && 'opacity-50 cursor-not-allowed'
            )}
          >
            <span className="flex items-center gap-2">
              {rulesSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {rulesSaving ? 'Saving...' : 'Save Rules'}
            </span>
          </button>
        </div>

        {/* Rule-save status messages */}
        {rulesSuccess && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-4 animate-scale-in">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-emerald-300">Rules saved successfully</p>
            </div>
          </div>
        )}
        {rulesError && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 mb-4 animate-scale-in">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-rose-300">{rulesError}</p>
            </div>
          </div>
        )}

        {/* ─── Rule list ─── */}
        <div className="space-y-3">
          {rules.length === 0 && (
            <div className="rounded-xl border border-dashed border-border/50 p-6 text-center">
              <p className="text-sm text-muted-foreground">
                No rules configured. Add a rule below to enable prompt replacements for this workflow.
              </p>
            </div>
          )}

          {rules.map((rule, index) => {
            const regexError = ruleRegexErrors[rule.id];
            return (
              <div
                key={rule.id}
                className={cn(
                  'rounded-xl border p-4 space-y-3',
                  rule.enabled
                    ? 'border-border/50 bg-secondary/30'
                    : 'border-border/30 bg-secondary/10 opacity-60'
                )}
              >
                {/* Row 1: reorder + stage + name + enabled + delete */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveRule(rule.id, -1)}
                      disabled={index === 0}
                      className={cn(
                        'p-1 rounded hover:bg-white/5 text-muted-foreground transition-colors',
                        index === 0 && 'opacity-30 cursor-not-allowed'
                      )}
                      title="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleMoveRule(rule.id, 1)}
                      disabled={index === rules.length - 1}
                      className={cn(
                        'p-1 rounded hover:bg-white/5 text-muted-foreground transition-colors',
                        index === rules.length - 1 && 'opacity-30 cursor-not-allowed'
                      )}
                      title="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <span className="text-xs font-mono text-muted-foreground select-none w-6">
                    #{index + 1}
                  </span>

                  <select
                    value={rule.stage}
                    onChange={(e) => handleUpdateRule(rule.id, { stage: e.target.value as 'pre_transform' | 'post_transform' })}
                    className="input-neural px-3 py-1.5 rounded-lg text-xs appearance-none cursor-pointer font-mono"
                    title="When to apply this rule"
                  >
                    <option value="pre_transform">pre_transform</option>
                    <option value="post_transform">post_transform</option>
                  </select>

                  <input
                    type="text"
                    value={rule.name || ''}
                    onChange={(e) => handleUpdateRule(rule.id, { name: e.target.value })}
                    placeholder="Rule name (optional)"
                    className="input-neural flex-1 min-w-[150px] px-3 py-1.5 rounded-lg text-sm"
                  />

                  {/* Enabled toggle */}
                  <button
                    onClick={() => handleUpdateRule(rule.id, { enabled: !rule.enabled })}
                    className={cn(
                      'relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0',
                      rule.enabled
                        ? 'bg-emerald-500'
                        : 'bg-secondary border border-border'
                    )}
                    title={rule.enabled ? 'Enabled' : 'Disabled'}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 shadow-sm',
                        rule.enabled && 'translate-x-5'
                      )}
                    />
                  </button>

                  <button
                    onClick={() => handleDeleteRule(rule.id)}
                    className="p-1.5 rounded-md text-rose-400/70 hover:text-rose-400 hover:bg-rose-500/10 transition-colors flex-shrink-0"
                    title="Delete rule"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Row 2: pattern */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Pattern {rule.isRegex && <span className="text-violet-400">(regex)</span>}
                  </label>
                  <input
                    type="text"
                    value={rule.pattern}
                    onChange={(e) => handleUpdateRule(rule.id, { pattern: e.target.value })}
                    className={cn(
                      'input-neural w-full px-3 py-2 rounded-lg text-sm font-mono',
                      regexError && 'border-rose-500/50'
                    )}
                    placeholder={rule.isRegex ? '\\b(sfw|nsfw)\\b' : 'sfw'}
                  />
                  {regexError && (
                    <p className="text-xs text-rose-400/90 mt-1 font-mono">{regexError}</p>
                  )}
                </div>

                {/* Row 3: replacement */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Replacement {rule.isRegex && <span className="text-violet-400">($1, $2 for capture groups)</span>}
                  </label>
                  <input
                    type="text"
                    value={rule.replacement}
                    onChange={(e) => handleUpdateRule(rule.id, { replacement: e.target.value })}
                    className="input-neural w-full px-3 py-2 rounded-lg text-sm font-mono"
                    placeholder="safe for work"
                  />
                </div>

                {/* Row 4: flag toggles */}
                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.isRegex}
                      onChange={(e) => handleUpdateRule(rule.id, { isRegex: e.target.checked })}
                    />
                    <span className="text-muted-foreground">Regex</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.caseInsensitive}
                      onChange={(e) => handleUpdateRule(rule.id, { caseInsensitive: e.target.checked })}
                    />
                    <span className="text-muted-foreground">Case-insensitive <code className="text-violet-400/70">i</code></span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.multiline}
                      onChange={(e) => handleUpdateRule(rule.id, { multiline: e.target.checked })}
                    />
                    <span className="text-muted-foreground">Multiline <code className="text-violet-400/70">m</code></span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.dotAll}
                      onChange={(e) => handleUpdateRule(rule.id, { dotAll: e.target.checked })}
                    />
                    <span className="text-muted-foreground">Dot-all <code className="text-violet-400/70">s</code></span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.unicode}
                      onChange={(e) => handleUpdateRule(rule.id, { unicode: e.target.checked })}
                    />
                    <span className="text-muted-foreground">Unicode <code className="text-violet-400/70">u</code></span>
                  </label>
                </div>
              </div>
            );
          })}

          {/* Add rule button */}
          <button
            onClick={handleAddRule}
            className="w-full rounded-xl border border-dashed border-border/50 hover:border-violet-500/50 hover:bg-violet-500/5 text-muted-foreground hover:text-violet-400 transition-colors py-3 flex items-center justify-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add rule
          </button>
        </div>

        {/* ─── Test Fire Panel ─── */}
        <div className="mt-6 pt-6 border-t border-border/30">
          <div className="flex items-center gap-2 mb-3">
            <Play className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold">Test Fire</h3>
            <span className="text-xs text-muted-foreground">
              (runs the current rules against a sample input via the real Worker code path)
            </span>
          </div>

          <textarea
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            placeholder="Sample image prompt to test rules against..."
            className="input-neural w-full px-3 py-2 rounded-lg text-sm font-mono resize-y mb-3"
            rows={3}
          />

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleRunTest}
              disabled={testRunning || !testInput.trim()}
              className={cn(
                'btn-secondary px-4 py-2 rounded-xl text-sm flex items-center gap-2',
                (testRunning || !testInput.trim()) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {testRunning ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {testRunning ? 'Running...' : 'Run Test'}
            </button>
            {testError && (
              <span className="text-sm text-rose-400 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                {testError}
              </span>
            )}
          </div>

          {/* Test Results */}
          {testResult && (
            <div className="mt-4 space-y-3">
              {!testResult.ok ? (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-rose-400">Validation error</p>
                      <p className="text-xs text-rose-300/80 mt-1 font-mono">{testResult.validation_error}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {(['pre_transform', 'post_transform'] as const).map((stageKey) => {
                    const stageResult = testResult[stageKey];
                    if (!stageResult) return null;
                    return (
                      <div key={stageKey} className="rounded-xl border border-border/50 bg-secondary/20 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-xs font-mono text-violet-400 bg-violet-500/10 px-2 py-0.5 rounded">
                            {stageKey}
                          </code>
                          <span className="text-xs text-muted-foreground">
                            {stageResult.fires.length} rule{stageResult.fires.length === 1 ? '' : 's'} fired &middot; {stageResult.totalDurationMs}ms
                            {stageResult.budgetExceeded && (
                              <span className="text-amber-400 ml-2">(budget exceeded &mdash; some rules skipped)</span>
                            )}
                          </span>
                        </div>

                        {stageResult.fires.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic">No rules applied to this stage.</p>
                        ) : (
                          <div className="space-y-2">
                            {stageResult.fires.map((fire) => (
                              <div key={fire.ruleId} className="rounded-lg bg-background/30 p-3">
                                <div className="flex items-center gap-2 mb-1.5 text-xs">
                                  <span className="font-medium">{fire.ruleName || '(unnamed)'}</span>
                                  <span className="text-muted-foreground">{fire.matchCount} match{fire.matchCount === 1 ? '' : 'es'}</span>
                                  <span className="text-muted-foreground">{fire.durationMs}ms</span>
                                  {fire.error && (
                                    <span className="text-rose-400 font-mono">{fire.error}</span>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-[10px] text-muted-foreground mb-0.5">input</p>
                                    <pre className="text-xs text-foreground/70 whitespace-pre-wrap font-mono bg-background/50 rounded p-2 break-all">{fire.input}</pre>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground mb-0.5">output</p>
                                    <pre className={cn(
                                      'text-xs whitespace-pre-wrap font-mono bg-background/50 rounded p-2 break-all',
                                      fire.output !== fire.input ? 'text-emerald-300' : 'text-foreground/70'
                                    )}>{fire.output}</pre>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {stageResult.skippedInvalid.length > 0 && (
                          <div className="mt-2 text-xs text-amber-400/90">
                            Skipped {stageResult.skippedInvalid.length} invalid rule{stageResult.skippedInvalid.length === 1 ? '' : 's'}:
                            <ul className="mt-1 ml-4 list-disc space-y-0.5 text-amber-300/70">
                              {stageResult.skippedInvalid.map((s, i) => (
                                <li key={i}><code className="font-mono">{s.ruleId}</code>: {s.reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <p className="text-xs font-medium text-emerald-300 mb-1">Final output</p>
                    <pre className="text-sm text-emerald-200 whitespace-pre-wrap font-mono break-all">{testResult.final_output}</pre>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
